import { AccordionContent, AccordionHeader, AccordionItem, AccordionTrigger, SidebarGroupLabel } from '@ibsys/kit';
import { MinusIcon, PlusIcon } from 'lucide-react';
import React, { FC, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PermissionCode, useHasAnyPermission } from '@/entities/profile';
import { cn, RouteConfig } from '@/shared/lib';
import { Icon, IconType } from '@/shared/ui';
import { calcGroupName, isActiveNested } from '@/widgets/MainLayout/model/utils';
import { AppSidebarItem } from './AppSidebarItem';
import { AppSidebarLabel } from './AppSidebarLabel';

type Props = {
  route: RouteConfig;
  name: string;
};

const getChildrenPermissions = (route: RouteConfig): (PermissionCode | undefined)[] => {
  if (route.children) {
    return [route.viewPermission, ...route.children.map(getChildrenPermissions)].flat();
  }
  return [route.viewPermission].flat();
};

export const AppSidebarGroup: FC<Props> = React.memo((props) => {
  const { route, name } = props;
  const { pathname } = useLocation();

  const [open, setOpen] = useState(() => isActiveNested(route, pathname));

  const childrenPermissions = useMemo(() => getChildrenPermissions(route), [route]);

  const allowView = useHasAnyPermission(childrenPermissions);

  if (!allowView) {
    return null;
  }

  return (
    <AccordionItem value={name} className={cn('flex flex-col overflow-hidden transition-[height] border-none p-0')}>
      {route.label && (
        <AccordionHeader>
          <AccordionTrigger hideIcon className={'p-0'}>
            <SidebarGroupLabel
              className={
                'uppercase p-2.5 flex items-center text-sidebar-foreground hover:text-sidebar-accent-foreground hover:bg-sidebar-accent cursor-pointer gap-2 w-full'
              }
              onClick={() => setOpen((prev) => !prev)}
            >
              {typeof route.navbar?.icon === 'string' ? (
                <Icon type={route.navbar?.icon as IconType} width={16} className={'text-muted-foreground'} />
              ) : (
                route.navbar?.icon
              )}
              <AppSidebarLabel label={route.label} />
              <div className={'ml-auto'}>
                {!open ? <PlusIcon size={14} strokeWidth={1.25} /> : <MinusIcon size={14} strokeWidth={1.25} />}
              </div>
            </SidebarGroupLabel>
          </AccordionTrigger>
        </AccordionHeader>
      )}
      <AccordionContent
        className={cn(
          'flex flex-col gap-1 ps-[20px] py-1 relative show',
          'group-data-[state=collapsed]:ps-0',
          'group-data-[state=expanded]:before:absolute group-data-[state=expanded]:before:start-[15px] group-data-[state=expanded]:before:top-0 group-data-[state=expanded]:before:bottom-0 group-data-[state=expanded]:before:border-s group-data-[state=expanded]:before:border-border ',
        )}
      >
        {route.children?.map((child, index) => {
          if (child.children?.length) {
            return <AppSidebarGroup key={child.path} route={child} name={calcGroupName(name, index)} />;
          }
          return !!child.navbar && <AppSidebarItem route={child} key={child.path} />;
        })}
        {/* Динамические пункты подменю группы (вычисляются в рантайме) */}
        {route.navbar?.dynamicChildren?.()}
      </AccordionContent>
    </AccordionItem>
  );
});
